import React, { useState } from 'react';
import { Button } from './ui/button';
import { apiService } from '../services/api';
import { toast } from 'sonner';
import { X, Loader2, FileText } from 'lucide-react';

export default function AddNoteModal({ studentId, studentName, onClose, onSave, initialText = '' }) {
  const today = new Date().toISOString().split('T')[0];
  const [noteDate, setNoteDate] = useState(today);
  const [noteText, setNoteText] = useState(initialText);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!noteText.trim()) {
      toast.error('Please enter note text');
      return;
    }

    setSaving(true);
    try {
      await apiService.createStudentNote({
        student_id: studentId,
        note_date: noteDate,
        note_text: noteText.trim()
      });
      toast.success('Note saved');
      onSave();
    } catch (error) {
      console.error('Failed to save note:', error);
      toast.error('Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#2C4C3B]/10 flex items-center justify-center">
              <FileText className="h-5 w-5 text-[#2C4C3B]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#1C1C1C]">Add Note</h2>
              <p className="text-sm text-[#595959]">{studentName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[#595959] hover:text-[#1C1C1C] p-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Date Field */}
          <div>
            <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">
              Date
            </label>
            <input
              type="date"
              value={noteDate}
              onChange={(e) => setNoteDate(e.target.value)}
              className="w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B]"
              required
              data-testid="note-date-input"
            />
            <p className="text-xs text-[#A3A3A3] mt-1">Defaults to today. Adjust if needed.</p>
          </div>

          {/* Note Text Field */}
          <div>
            <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">
              Note
            </label>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Enter note text..."
              rows={6}
              className="w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] resize-y"
              required
              data-testid="note-text-input"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || !noteText.trim()}
              className="bg-[#2C4C3B] hover:bg-[#1E3629] rounded-xl"
              data-testid="save-note-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Note'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
